import api from '@/libs/axiosInstance';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface MetaAdsAuthorizeResponse {
  redirectUrl: string;
}

interface ConnectMetaAdsInput {
  code: string;
  state: string;
}

/**
 * Starts the ads Business Login: asks the backend for the brand-bound dialog
 * URL. The component redirects to it; Meta returns to the integrations page with
 * `?code=&state=ma_...`, which `useConnectMetaAds` exchanges.
 */
export const useMetaAdsAuthorize = () => {
  return useMutation({
    mutationFn: async (): Promise<MetaAdsAuthorizeResponse> => {
      const response = await api.get('/dashboard/integrations/meta-ads/authorize');
      return response.data.data;
    },
  });
};

/**
 * Completes the connection by posting the dialog `code` and brand-bound `state`
 * back to the backend, which verifies the state and exchanges the code for the
 * durable ads token.
 */
export const useConnectMetaAds = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ code, state }: ConnectMetaAdsInput): Promise<void> => {
      await api.post('/dashboard/integrations/meta-ads/connect', { code, state });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
};

export const useDisconnectMetaAds = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await api.delete(`/dashboard/integrations/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
};

/**
 * The brand-level launch preferences recorded on the Meta ads integration. Every
 * field is optional and merged server-side: a call omitting one keeps the stored
 * value, so each surface sends only what it edits.
 */
export interface MetaAdsPreferencesInput {
  /** The account billed by default when a boost launches. */
  adAccountId?: string;
  /** The entity the ads run for, required by the EU Digital Services Act. */
  dsaBeneficiary?: string;
  /** The paying entity when it differs from the beneficiary; null clears it. */
  dsaPayor?: string | null;
}

/**
 * Persists the brand's Meta launch preferences (default ad account and DSA
 * advertiser declaration). Invalidates the integrations query so the stored
 * values refresh; callers reading them from elsewhere (the boost readiness)
 * invalidate their own query in `onSuccess`.
 */
export const useSaveMetaAdsPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: MetaAdsPreferencesInput): Promise<void> => {
      await api.put('/dashboard/integrations/meta-ads/default-account', input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
};
